import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useToast } from '../../hooks/useToast';
import {
  ChangeRequest,
  ChangeRequestStatus,
  EntityType,
} from '../../types/changeRequest';
import { changeRequestService } from '../../services/changeRequests';
import { enumMatches, tryNormalizeEnumValue } from '../../utils/enumNormalize';
import {
  formatChangeRequestFieldName,
  formatChangeRequestFieldValue,
} from '../../utils/formatChangeField';
import Modal from '../../components/common/Modal';
import FormField from '../../components/common/FormField';
import { Container, Stack } from '../../design-system/components/Layout';
import { Card } from '../../design-system/components/Card';
import { Button } from '../../design-system/components/Button';
import { Heading, Text, Label } from '../../design-system/components/Typography';
import { PageHeader } from '../../design-system/patterns/PageHeader';
import { DataTable, Column } from '../../components/common/DataTable';
import SearchBar from '../../components/common/SearchBar';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { formatAthensDateTime } from '../../utils/athensTime';
import { spacing } from '../../design-system/tokens/spacing';
import { typography } from '../../design-system/tokens/typography';
import { borders } from '../../design-system/tokens/borders';
import { shadows } from '../../design-system/tokens/shadows';

const ChangeRequestsContainer = styled(Container)`
  max-width: 1400px;
`;

const FiltersSection = styled(Card)`
  padding: ${spacing[6]};
  margin-bottom: ${spacing[6]};
  border-radius: ${borders.radius.lg};
`;

const FiltersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${spacing[5]};
  align-items: end;
`;

const FilterLabel = styled.label`
  font-weight: ${typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text};
  font-size: ${typography.fontSize.sm};
  font-family: ${typography.fontFamily.primary};
  display: block;
  margin-bottom: ${spacing[1]};
`;

const SelectStyled = styled.select`
  padding: ${spacing[3]} ${spacing[4]};
  border: ${borders.width.base} solid ${props => props.theme.colors.border};
  border-radius: ${borders.radius.lg};
  font-size: ${typography.fontSize.sm};
  background: ${props => props.theme.colors.surface};
  color: ${props => props.theme.colors.text};
  font-family: ${typography.fontFamily.primary};
  width: 100%;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: ${props => shadows.focus(props.theme.colors.primary)};
  }
`;

const RejectModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[4]};
`;

/** One row = all pending changes from same user for same entity */
export interface ChangeRequestGroup {
  id: string;
  userId: string;
  entityId: string;
  entityType: EntityType;
  userEmail: string;
  requestedAt: string;
  items: ChangeRequest[];
}

const groupKey = (cr: ChangeRequest) =>
  `${cr.userId}-${cr.entityId}-${cr.entityType}`;

function groupChangeRequests(requests: ChangeRequest[]): ChangeRequestGroup[] {
  const map = new Map<string, ChangeRequest[]>();
  for (const cr of requests) {
    const key = groupKey(cr);
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(cr);
  }
  const groups: ChangeRequestGroup[] = [];
  map.forEach((items, key) => {
    const latest = items.reduce((a, b) =>
      new Date(b.requestedAt) > new Date(a.requestedAt) ? b : a
    );
    groups.push({
      id: key,
      userId: latest.userId,
      entityId: latest.entityId,
      entityType: latest.entityType,
      userEmail: latest.userEmail ?? latest.userId,
      requestedAt: latest.requestedAt,
      items: items.sort(
        (a, b) => new Date(b.requestedAt).getTime() - new Date(a.requestedAt).getTime()
      ),
    });
  });
  return groups.sort(
    (a, b) => new Date(b.requestedAt).getTime() - new Date(a.requestedAt).getTime()
  );
}

const OldValueCell = styled.span`
  color: ${props => props.theme.colors.textMuted};
  font-size: ${typography.fontSize.sm};
  font-style: italic;
`;

const NewValueCell = styled.span`
  color: ${props => props.theme.colors.primary};
  font-weight: ${typography.fontWeight.semibold};
  font-size: ${typography.fontSize.sm};
  padding: 2px 6px;
  border-radius: ${borders.radius.sm};
  background: ${props => `${props.theme.colors.primary}15`};
`;

const CurrentValueCell = styled.span`
  color: ${props => props.theme.colors.textSecondary};
  font-size: ${typography.fontSize.sm};
`;

const DetailTableWrap = styled.div`
  border-radius: ${borders.radius.lg};
  border: 1px solid ${props => props.theme.colors.border};
  overflow: hidden;
  margin-top: ${spacing[3]};
`;

const DetailTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: ${typography.fontSize.sm};
  background: ${props => props.theme.colors.surface};
`;
const DetailTh = styled.th`
  text-align: left;
  padding: ${spacing[3]} ${spacing[4]};
  background: ${props => props.theme.colors.surfaceHover};
  color: ${props => props.theme.colors.textSecondary};
  font-weight: ${typography.fontWeight.semibold};
  font-size: ${typography.fontSize.xs};
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;
const DetailTd = styled.td`
  padding: ${spacing[3]} ${spacing[4]};
  border-bottom: 1px solid ${props => props.theme.colors.border};
  vertical-align: top;
  &:first-child {
    font-weight: ${typography.fontWeight.medium};
    color: ${props => props.theme.colors.text};
  }
`;
const DetailTr = styled.tr<{ $superseded?: boolean }>`
  opacity: ${props => (props.$superseded ? 0.55 : 1)};
  &:last-child td {
    border-bottom: none;
  }
  &:nth-child(even) td {
    background: ${props => props.theme.colors.surfaceHover};
  }
`;

const PrecedenceNote = styled(Text)`
  margin-top: ${spacing[2]};
`;

function chronologicalIds(items: ChangeRequest[]): string[] {
  return [...items]
    .sort(
      (a, b) => new Date(a.requestedAt).getTime() - new Date(b.requestedAt).getTime()
    )
    .map(item => item.id);
}

function latestIdsByField(items: ChangeRequest[]): Set<string> {
  const latest = new Map<string, ChangeRequest>();
  for (const item of items) {
    const key = item.fieldName.trim().toLowerCase();
    const prev = latest.get(key);
    if (
      !prev ||
      new Date(item.requestedAt).getTime() > new Date(prev.requestedAt).getTime()
    ) {
      latest.set(key, item);
    }
  }
  return new Set(Array.from(latest.values()).map(item => item.id));
}
const DetailActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing[3]};
  margin-top: ${spacing[5]};
  padding-top: ${spacing[5]};
  border-top: 1px solid ${props => props.theme.colors.border};
  justify-content: flex-end;
`;

const DetailHeader = styled.div`
  padding: ${spacing[4]};
  background: ${props => props.theme.colors.surfaceHover};
  border-radius: ${borders.radius.md};
  margin-bottom: ${spacing[2]};
`;
const DetailMeta = styled(Text)`
  font-size: ${typography.fontSize.sm};
`;

const ActionButtonsWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing[2]};
  align-items: center;
`;

const ChangeRequestsAdmin: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { success: showSuccess, error: showError } = useToast();
  const [changeRequests, setChangeRequests] = useState<ChangeRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [entityTypeFilter, setEntityTypeFilter] = useState<EntityType | 'all'>('all');
  const [selectedGroup, setSelectedGroup] = useState<ChangeRequestGroup | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [processingGroupId, setProcessingGroupId] = useState<string | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const data = await changeRequestService.getPendingChangeRequests();
        setChangeRequests(data);
      } catch (err) {
        console.error('Error loading change requests:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const filtered = useMemo(() => {
    let list = changeRequests;
    if (entityTypeFilter !== 'all')
      list = list.filter(cr => enumMatches(EntityType, cr.entityType, entityTypeFilter));
    if (debouncedSearch.trim()) {
      const q = debouncedSearch.toLowerCase();
      list = list.filter(
        cr =>
          (cr.userEmail?.toLowerCase().includes(q)) ||
          cr.fieldName.toLowerCase().includes(q) ||
          (cr.oldValue?.toLowerCase().includes(q)) ||
          (cr.newValue?.toLowerCase().includes(q))
      );
    }
    return list;
  }, [changeRequests, entityTypeFilter, debouncedSearch]);

  const groups = useMemo(() => groupChangeRequests(filtered), [filtered]);

  const getEntityTypeLabel = (type?: EntityType | string | number | null): string => {
    const normalized = tryNormalizeEnumValue(EntityType, type);
    switch (normalized) {
      case EntityType.IndividualMember:
        return t('changeRequests.individualMember');
      case EntityType.Institution:
        return t('changeRequests.institution');
      default:
        return '—';
    }
  };

  const formatDate = (dateString: string) => formatAthensDateTime(dateString, i18n.language);

  const openDetail = (group: ChangeRequestGroup) => {
    setSelectedGroup(group);
    setShowDetailsModal(true);
  };

  const handleAcceptBatch = async (group?: ChangeRequestGroup | null) => {
    const g = group ?? selectedGroup;
    if (!g) return;
    setProcessingGroupId(g.id);
    try {
      await changeRequestService.approveChangeRequestsBatch(chronologicalIds(g.items));
      showSuccess(t('changeRequests.approved'));
      setShowDetailsModal(false);
      setSelectedGroup(null);
      const data = await changeRequestService.getPendingChangeRequests();
      setChangeRequests(data);
    } catch (err) {
      showError(err instanceof Error ? err.message : t('changeRequests.approveError'));
    } finally {
      setProcessingGroupId(null);
    }
  };

  const openRejectModal = (group?: ChangeRequestGroup | null) => {
    if (group) setSelectedGroup(group);
    setShowDetailsModal(false);
    setShowRejectModal(true);
  };

  const handleRejectBatch = async () => {
    if (!selectedGroup || !rejectionReason.trim()) return;
    setProcessingGroupId(selectedGroup.id);
    try {
      await changeRequestService.rejectChangeRequestsBatch(
        selectedGroup.items.map(i => i.id),
        rejectionReason.trim()
      );
      showSuccess(t('changeRequests.rejected'));
      setRejectionReason('');
      setShowRejectModal(false);
      setSelectedGroup(null);
      const data = await changeRequestService.getPendingChangeRequests();
      setChangeRequests(data);
    } catch (err) {
      showError(err instanceof Error ? err.message : t('changeRequests.rejectError'));
    } finally {
      setProcessingGroupId(null);
    }
  };

  const closeRejectModal = () => {
    setShowRejectModal(false);
    setRejectionReason('');
    setSelectedGroup(null);
  };

  const columns: Column<ChangeRequestGroup>[] = [
    {
      key: 'user',
      header: t('changeRequests.userEmail'),
      render: (g) => g.userEmail || g.userId,
    },
    {
      key: 'entityType',
      header: t('changeRequests.entityType'),
      render: (g) => getEntityTypeLabel(g.entityType),
    },
    {
      key: 'requestedAt',
      header: t('changeRequests.requestedAt'),
      render: (g) => formatDate(g.requestedAt),
    },
    {
      key: 'count',
      header: t('changeRequests.changesCount'),
      render: (g) => g.items.length,
    },
    {
      key: 'actions',
      header: t('admin.actions'),
      actions: (g) => (
        <ActionButtonsWrap>
          <Button
            variant="outline"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              openDetail(g);
            }}
          >
            {t('changeRequests.viewDetails')}
          </Button>
          <Button
            variant="gradient"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              handleAcceptBatch(g);
            }}
            disabled={!!processingGroupId}
          >
            {t('changeRequests.approve')}
          </Button>
          <Button
            variant="danger"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              openRejectModal(g);
            }}
            disabled={!!processingGroupId}
          >
            {t('changeRequests.reject')}
          </Button>
        </ActionButtonsWrap>
      ),
    },
  ];

  return (
    <ChangeRequestsContainer>
      <Stack direction="column" spacing="xl">
        <PageHeader
          title={t('changeRequests.pendingRequests')}
          subtitle={
            !loading
              ? t('changeRequests.showingResults', {
                  count: groups.length,
                  total: changeRequests.length,
                }) || `Showing ${groups.length} request(s), ${changeRequests.length} change(s)`
              : undefined
          }
          pattern={true}
        />

        <FiltersSection variant="elevated" accentBorder="top" pattern="dots" patternIntensity="subtle">
          <Stack direction="column" spacing="md">
            <Heading variant="h3" color="text">
              {t('changeRequests.filters')}
            </Heading>
            <FiltersGrid>
              <Stack direction="column" spacing="sm">
                <FilterLabel htmlFor="admin-changerequests-search">{t('changeRequests.searchPlaceholder')}</FilterLabel>
                <SearchBar
                  id="admin-changerequests-search"
                  value={search}
                  onChange={setSearch}
                  onSearch={setDebouncedSearch}
                  placeholder={t('changeRequests.searchPlaceholder')}
                  fullWidth
                />
              </Stack>
              <Stack direction="column" spacing="sm">
                <FilterLabel htmlFor="admin-changerequests-entity">{t('changeRequests.filterByEntity')}</FilterLabel>
                <SelectStyled
                  id="admin-changerequests-entity"
                  value={entityTypeFilter === 'all' ? 'all' : entityTypeFilter.toString()}
                  onChange={(e) =>
                    setEntityTypeFilter(
                      e.target.value === 'all' ? 'all' : (parseInt(e.target.value) as EntityType)
                    )
                  }
                >
                  <option value="all">{t('changeRequests.allEntities')}</option>
                  <option value={EntityType.IndividualMember.toString()}>
                    {t('changeRequests.individualMember')}
                  </option>
                  <option value={EntityType.Institution.toString()}>
                    {t('changeRequests.institution')}
                  </option>
                </SelectStyled>
              </Stack>
              <Button
                variant="outline"
                onClick={() => {
                  setSearch('');
                  setDebouncedSearch('');
                  setEntityTypeFilter('all');
                }}
              >
                {t('changeRequests.clearFilters')}
              </Button>
            </FiltersGrid>
          </Stack>
        </FiltersSection>

        {loading ? (
          <LoadingSpinner fullPage />
        ) : (
          <DataTable<ChangeRequestGroup>
            data={groups}
            columns={columns}
            loading={false}
            emptyMessage={t('changeRequests.noPendingRequests')}
            onRowClick={openDetail}
          />
        )}

        {/* Detail modal: all changes in this group with old / new / current */}
        <Modal
          isOpen={showDetailsModal}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedGroup(null);
          }}
          title={t('changeRequests.requestDetails')}
          footer={
            selectedGroup && (
              <DetailActions>
                <Button
                  variant="gradient"
                  onClick={() => handleAcceptBatch()}
                  disabled={!!processingGroupId}
                >
                  {t('changeRequests.approve')}
                </Button>
                <Button variant="danger" onClick={() => openRejectModal(selectedGroup)} disabled={!!processingGroupId}>
                  {t('changeRequests.reject')}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowDetailsModal(false);
                    setSelectedGroup(null);
                  }}
                >
                  {t('common.close')}
                </Button>
              </DetailActions>
            )
          }
        >
          {selectedGroup && (
            <Stack direction="column" spacing="lg">
              <DetailHeader>
                <DetailMeta color="secondary">
                  {selectedGroup.userEmail} · {getEntityTypeLabel(selectedGroup.entityType)} ·{' '}
                  {formatDate(selectedGroup.requestedAt)}
                </DetailMeta>
                <Text size="xs" color="secondary" style={{ marginTop: spacing[1] }}>
                  {selectedGroup.items.length} {t('changeRequests.changesCount')}
                </Text>
                <PrecedenceNote size="sm" color="secondary">
                  {t('changeRequests.latestTakesPrecedence', {
                    defaultValue:
                      'When the same field appears more than once, the latest requested value is applied.',
                  })}
                </PrecedenceNote>
              </DetailHeader>
              <DetailTableWrap>
                <DetailTable>
                  <thead>
                    <tr>
                      <DetailTh>{t('changeRequests.fieldName')}</DetailTh>
                      <DetailTh>{t('changeRequests.oldValue')}</DetailTh>
                      <DetailTh>{t('changeRequests.newValue')}</DetailTh>
                      <DetailTh>{t('changeRequests.currentValue')}</DetailTh>
                      <DetailTh>{t('changeRequests.precedence', { defaultValue: 'Precedence' })}</DetailTh>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const latestIds = latestIdsByField(selectedGroup.items);
                      return selectedGroup.items.map(cr => {
                      const isLatest = latestIds.has(cr.id);
                      return (
                      <DetailTr key={cr.id} $superseded={!isLatest}>
                        <DetailTd>{formatChangeRequestFieldName(cr.fieldName, t)}</DetailTd>
                        <DetailTd>
                          <OldValueCell>
                            {formatChangeRequestFieldValue(cr.fieldName, cr.oldValue, t)}
                          </OldValueCell>
                        </DetailTd>
                        <DetailTd>
                          <NewValueCell>
                            {formatChangeRequestFieldValue(cr.fieldName, cr.newValue, t)}
                          </NewValueCell>
                        </DetailTd>
                        <DetailTd>
                          <CurrentValueCell>
                            {formatChangeRequestFieldValue(cr.fieldName, cr.currentValue, t)}
                          </CurrentValueCell>
                        </DetailTd>
                        <DetailTd>
                          {isLatest
                            ? t('changeRequests.willApply', { defaultValue: 'Will apply' })
                            : t('changeRequests.superseded', { defaultValue: 'Superseded' })}
                        </DetailTd>
                      </DetailTr>
                    );
                      });
                    })()}
                  </tbody>
                </DetailTable>
              </DetailTableWrap>
            </Stack>
          )}
        </Modal>

        <Modal
          isOpen={showRejectModal}
          onClose={closeRejectModal}
          title={t('changeRequests.rejectRequest')}
          footer={
            <>
              <Button variant="outline" onClick={closeRejectModal}>
                {t('common.cancel')}
              </Button>
              <Button
                variant="danger"
                onClick={handleRejectBatch}
                disabled={!rejectionReason.trim() || !!processingGroupId}
              >
                {t('changeRequests.reject')}
              </Button>
            </>
          }
        >
          <RejectModalContent>
            <Label>
              {t('changeRequests.rejectionReason')} *
            </Label>
            <FormField
              textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder={
                t('changeRequests.rejectionReasonPlaceholder')
              }
              required
            />
          </RejectModalContent>
        </Modal>
      </Stack>
    </ChangeRequestsContainer>
  );
};

export default ChangeRequestsAdmin;
